// src/components/store/sections/featured-products/FloatingCircleProductCard.tsx
"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useCart } from "@/context/CartContext";

export interface ProductItem {
  id: number | string;
  name: string;
  slug?: string;
  price: number | string;
  regular_price?: number | string;
  image?: string | null;
  primary_image?: string | null;
  category_name?: string;
  description?: string;
  rating?: number;
}

// 🌟 Screenshot-er 3-ti theme (Vibrant Purple, Hot Magenta, Sunny Yellow)
const CARD_THEMES = [
  {
    bottomBg: "bg-[#5b36f5]",
    circleBg: "bg-[#eeebff]",
    textColor: "text-white/80",
    priceColor: "text-[#22d3ee]",
    btnText: "text-[#5b36f5]",
    fallbackImg: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=500&auto=format&fit=crop&q=80",
  },
  {
    bottomBg: "bg-[#f4208a]",
    circleBg: "bg-[#fdebf4]",
    textColor: "text-white/85",
    priceColor: "text-white",
    btnText: "text-[#f4208a]",
    fallbackImg: "https://images.unsplash.com/photo-1503602642458-232111445657?w=500&auto=format&fit=crop&q=80",
  },
  {
    bottomBg: "bg-[#f8bb22]",
    circleBg: "bg-[#fef8e7]",
    textColor: "text-[#78350f]/80",
    priceColor: "text-[#9a3412]",
    btnText: "text-[#b45309]",
    fallbackImg: "https://images.unsplash.com/photo-1485955900006-10f4d324d411?w=500&auto=format&fit=crop&q=80",
  },
  {
    bottomBg: "bg-[#10b981]",
    circleBg: "bg-[#ecfdf5]",
    textColor: "text-white/85",
    priceColor: "text-[#fef08a]",
    btnText: "text-[#059669]",
    fallbackImg: "https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=500&auto=format&fit=crop&q=80",
  },
];

interface FloatingCircleProductCardProps {
  product: ProductItem;
  index?: number;
}

export default function FloatingCircleProductCard({
  product,
  index = 0,
}: FloatingCircleProductCardProps) {
  const [loading, setLoading] = useState(false);
  const cartContext = useCart?.() as any;

  const theme = CARD_THEMES[index % CARD_THEMES.length];

  const formatImageUrl = (img?: string | null) => {
    if (!img) return null;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    return `http://127.0.0.1:8000${img.startsWith("/") ? "" : "/"}${img}`;
  };

  const productImg =
    formatImageUrl(product.primary_image || product.image) || theme.fallbackImg;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);

    if (cartContext && typeof cartContext.addToCart === "function") {
      cartContext.addToCart(product, 1);
      if (typeof cartContext.openCart === "function") cartContext.openCart();
    } else {
      window.dispatchEvent(
        new CustomEvent("addToCart", { detail: { product, quantity: 1 } })
      );
      window.dispatchEvent(new CustomEvent("openCartDrawer"));
    }

    setTimeout(() => setLoading(false), 500);
  };

  const priceNum = Number(product.price) || 0;

  return (
    <div className="w-full rounded-[28px] overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col justify-between group bg-white border border-slate-100 select-none">
      
      {/* 🌟 1. Upper Half: White Area with Pastel Circle & Product Cutout */}
      <div className="relative pt-6 pb-5 px-4 flex flex-col items-center justify-center bg-white">
        
        {/* Soft Circular Backdrop */}
        <div className={`relative w-36 h-36 sm:w-40 sm:h-40 rounded-full ${theme.circleBg} flex items-center justify-center transition-transform duration-500 group-hover:scale-105`}>
          <Link href={`/products/${product.slug || product.id}`} className="relative w-32 h-32 sm:w-36 sm:h-36 drop-shadow-xl flex items-center justify-center">
            <Image
              src={productImg}
              alt={product.name}
              fill
              unoptimized
              className="object-contain p-1"
            />
          </Link>
        </div>

        {/* Product Title */}
        <Link href={`/products/${product.slug || product.id}`} className="mt-4 block text-center max-w-[200px]">
          <h3 className="text-sm sm:text-base font-black text-slate-900 tracking-tight truncate hover:underline">
            {product.name || "Product Name"}
          </h3>
        </Link>
      </div>

      {/* 🌟 2. Lower Half: Vibrant Colored Block with Description, Price & Pill Button */}
      <div className={`p-5 sm:p-6 text-center flex flex-col items-center justify-between space-y-3.5 ${theme.bottomBg} transition-colors duration-300`}>
        {/* Description */}
        <p className={`text-[10px] sm:text-[11px] font-medium leading-relaxed line-clamp-3 max-w-[220px] ${theme.textColor}`}>
          {product.description ||
            "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet."}
        </p>

        {/* Price Display */}
        <div className={`text-xl sm:text-2xl font-black tracking-tight ${theme.priceColor}`}>
          ${Math.round(priceNum).toLocaleString()}.0
        </div>

        {/* Pill "ADD TO CART" Button */}
        <button
          type="button"
          onClick={handleAddToCart}
          disabled={loading}
          className={`px-5 py-2 rounded-full bg-white ${theme.btnText} font-black text-[10px] sm:text-[11px] tracking-wider uppercase transition-all shadow-md hover:bg-slate-50 active:scale-95 cursor-pointer disabled:opacity-60`}
        >
          {loading ? "Adding..." : "ADD TO CART"}
        </button>
      </div>

    </div>
  );
}